/**
 * File System Access API Helper
 * Save and export recipes, shopping lists, etc.
 */

/**
 * Check if File System Access API is available
 */
export function isFileSystemAccessAvailable() {
  return 'showSaveFilePicker' in window;
}

/**
 * Save text file
 */
export async function saveTextFile(content, defaultFileName = 'export.txt', mimeType = 'text/plain') {
  if (isFileSystemAccessAvailable()) {
    return await saveFileModern(content, defaultFileName, mimeType);
  } else {
    return saveFileFallback(content, defaultFileName, mimeType);
  }
}

/**
 * Modern File System Access API
 */
async function saveFileModern(content, fileName, mimeType) {
  try {
    const opts = {
      suggestedName: fileName,
      types: [{
        description: 'Text file',
        accept: { [mimeType]: [`.${fileName.split('.').pop()}`] }
      }]
    };

    const handle = await window.showSaveFilePicker(opts);
    const writable = await handle.createWritable();
    await writable.write(content);
    await writable.close();

    console.log(`💾 File saved: ${fileName}`);
    return { success: true, fileName };
  } catch (error) {
    // User cancelled
    if (error.name === 'AbortError') {
      return { success: false, cancelled: true };
    }

    console.error('Error saving file:', error);
    return { success: false, error: error.message, fallback: true };
  }
}

/**
 * Fallback download method
 */
function saveFileFallback(content, fileName, mimeType) {
  try {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    console.log(`💾 File downloaded: ${fileName}`);
    return { success: true, fileName };
  } catch (error) {
    console.error('Error downloading file:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Export shopping list as text file
 */
export async function exportShoppingList(items, familyName = 'Family') {
  const text = formatShoppingListText(items);
  const fileName = `${familyName}-Shopping-List-${new Date().toISOString().split('T')[0]}.txt`;
  
  return await saveTextFile(text, fileName);
}

/**
 * Export shopping list as CSV
 */
export async function exportShoppingListCSV(items, familyName = 'Family') {
  const csv = formatShoppingListCSV(items);
  const fileName = `${familyName}-Shopping-List-${new Date().toISOString().split('T')[0]}.csv`;
  
  return await saveTextFile(csv, fileName, 'text/csv');
}

/**
 * Export recipe as text file
 */
export async function exportRecipe(recipe) {
  const text = formatRecipeText(recipe);
  const fileName = `${recipe.title.replace(/[^a-z0-9]/gi, '-')}-Recipe.txt`;
  
  return await saveTextFile(text, fileName);
}

/**
 * Export recipe as JSON
 */
export async function exportRecipeJSON(recipe) {
  const json = JSON.stringify(recipe, null, 2);
  const fileName = `${recipe.title.replace(/[^a-z0-9]/gi, '-')}-Recipe.json`;
  
  return await saveTextFile(json, fileName, 'application/json');
}

/**
 * Export all recipes as JSON
 */
export async function exportAllRecipes(recipes, familyName = 'Family') {
  const json = JSON.stringify(recipes, null, 2);
  const fileName = `${familyName}-Recipes-${new Date().toISOString().split('T')[0]}.json`;
  
  return await saveTextFile(json, fileName, 'application/json');
}

/**
 * Import recipes from JSON file
 */
export async function importRecipesJSON() {
  try {
    if ('showOpenFilePicker' in window) {
      const [handle] = await window.showOpenFilePicker({
        types: [{
          description: 'JSON files',
          accept: { 'application/json': ['.json'] }
        }],
        multiple: false
      });

      const file = await handle.getFile();
      const text = await file.text();
      const recipes = JSON.parse(text);

      console.log(`📥 Imported ${recipes.length} recipe(s)`);
      return { success: true, recipes };
    } else {
      // Fallback to file input
      return await importFileInput('application/json');
    }
  } catch (error) {
    if (error.name === 'AbortError') {
      return { success: false, cancelled: true };
    }

    console.error('Error importing recipes:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Fallback file input for import
 */
function importFileInput(accept) {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;

    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (file) {
        try {
          const text = await file.text();
          const data = JSON.parse(text);
          resolve({ success: true, recipes: Array.isArray(data) ? data : [data] });
        } catch (error) {
          resolve({ success: false, error: error.message });
        }
      } else {
        resolve({ success: false, cancelled: true });
      }
    };

    input.click();
  });
}

// ==================== FORMATTING FUNCTIONS ====================

/**
 * Format shopping list as text
 */
function formatShoppingListText(items) {
  let text = '🛒 SHOPPING LIST\n';
  text += `Generated: ${new Date().toLocaleString()}\n`;
  text += '=';//50;
  text += '\n\n';

  // Group by category
  const grouped = items.reduce((acc, item) => {
    const category = item.category || 'Other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(item);
    return acc;
  }, {});

  Object.entries(grouped).forEach(([category, categoryItems]) => {
    text += `${category.toUpperCase()}\n`;
    text += '-'.repeat(category.length) + '\n';
    
    categoryItems.forEach((item, index) => {
      text += `${index + 1}. ${item.name}`;
      if (item.quantity) text += ` (${item.quantity})`;
      if (item.store) text += ` - ${item.store}`;
      if (item.price) text += ` - $${item.price.toFixed(2)}`;
      text += '\n';
    });
    
    text += '\n';
  });

  // Total
  const total = items.reduce((sum, item) => sum + (item.price || 0), 0);
  if (total > 0) {
    text += `\nTotal: $${total.toFixed(2)}\n`;
  }

  text += '\n📱 Created with Family Hub\n';

  return text;
}

/**
 * Format shopping list as CSV
 */
function formatShoppingListCSV(items) {
  let csv = 'Name,Quantity,Category,Store,Price,Approved\n';
  
  items.forEach(item => {
    csv += `"${item.name || ''}",`;
    csv += `"${item.quantity || ''}",`;
    csv += `"${item.category || ''}",`;
    csv += `"${item.store || ''}",`;
    csv += `"${item.price || ''}",`;
    csv += `"${item.approved ? 'Yes' : 'No'}"\n`;
  });

  return csv;
}

/**
 * Format recipe as text
 */
function formatRecipeText(recipe) {
  let text = `📝 ${recipe.title.toUpperCase()}\n`;
  text += '='.repeat(recipe.title.length + 3) + '\n\n';

  if (recipe.description) {
    text += `${recipe.description}\n\n`;
  }

  // Metadata
  const metadata = [];
  if (recipe.prepTime) metadata.push(`⏱️ Prep: ${recipe.prepTime} min`);
  if (recipe.cookTime) metadata.push(`🍳 Cook: ${recipe.cookTime} min`);
  if (recipe.servings) metadata.push(`🍽️ Servings: ${recipe.servings}`);
  if (recipe.difficulty) metadata.push(`📊 Difficulty: ${recipe.difficulty}`);
  
  if (metadata.length > 0) {
    text += metadata.join(' | ') + '\n\n';
  }

  // Ingredients
  if (recipe.ingredients && recipe.ingredients.length > 0) {
    text += '📋 INGREDIENTS\n';
    text += '-'.repeat(15) + '\n';
    recipe.ingredients.forEach(ing => {
      text += `• ${ing.amount || ''} ${ing.unit || ''} ${ing.name}`;
      if (ing.notes) text += ` (${ing.notes})`;
      text += '\n';
    });
    text += '\n';
  }

  // Instructions
  if (recipe.instructions && recipe.instructions.length > 0) {
    text += '👨‍🍳 INSTRUCTIONS\n';
    text += '-'.repeat(15) + '\n';
    recipe.instructions.forEach((step, index) => {
      text += `${index + 1}. ${step}\n`;
    });
    text += '\n';
  }

  // Tips
  if (recipe.tips && recipe.tips.length > 0) {
    text += '💡 TIPS\n';
    text += '-'.repeat(15) + '\n';
    recipe.tips.forEach(tip => {
      text += `• ${tip}\n`;
    });
    text += '\n';
  }

  text += `\n📱 Created with Family Hub\n`;
  text += `Generated: ${new Date().toLocaleString()}\n`;

  return text;
}

/**
 * Read directory (for future features)
 */
export async function readDirectory() {
  if (!('showDirectoryPicker' in window)) {
    return {
      success: false,
      error: 'Directory picker not available'
    };
  }

  try {
    const handle = await window.showDirectoryPicker();
    const files = [];

    for await (const entry of handle.values()) {
      if (entry.kind === 'file') {
        files.push({
          name: entry.name,
          handle: entry
        });
      }
    }

    return { success: true, files };
  } catch (error) {
    if (error.name === 'AbortError') {
      return { success: false, cancelled: true };
    }

    return { success: false, error: error.message };
  }
}












